import { useState } from 'react';
import API from 'src/API';
import { ICertificateInfo } from 'src/Types/Certificate';
import { AttachmentType } from 'src/Types/File/Attachment';
import useModal from './useModal';
import useToggle from './useToggle';

type SendSoapSignData = {
	mode: 'doubleSp' | 'base';
	digest1: string;
	digest2: string;
	fileName1: string;
	fileName2: string;
	componentId: string;
	attachmentId: string;
	mr: string;
	requestId: string;
	signingTag: string;
	withId: string;
	digest: string;
	digestArray: string;
	fillInTheEnd: string;
	callbackUrl: string;
	isOv: boolean;
};

type UseSendSoapSignAttachmentArgs = {
	fileRecId: string;
	info: AttachmentType | null;
	onReload: () => void;
	needReload: () => void;
};

const parseSendSoapSignHtml = (html: string): SendSoapSignData => {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	const inputRegex = /<input[^>]*>/gi;
	const attrRegex = /(\w+)\s*=\s*["']([^"']*)["']/gi;
	const map: Record<string, string> = {};
	const inputs = html.match(inputRegex) ?? [];
	for (const input of inputs) {
		const attrs: Record<string, string> = {};
		let match: RegExpExecArray | null;
		while ((match = attrRegex.exec(input))) {
			attrs[match[1].toLowerCase()] = match[2];
		}
		const key = (attrs.id || attrs.name || '').toLowerCase();
		if (key && attrs.value !== undefined) {
			map[key] = attrs.value;
		}
	}
	const getValue = (id: string) => {
		const direct = doc.getElementById(id)?.getAttribute('value') ?? '';
		if (direct) {
			return direct;
		}
		return map[id.toLowerCase()] ?? '';
	};
	const callbackMatch = html.match(
		/browseCertificate\(\s*["']([^"']+)["']\s*,\s*["']([^"']+)["']\s*,\s*["']([^"']+)["']/i,
	);
	const callbackUrl = callbackMatch?.[1] ?? '';
	const isOvRaw = callbackMatch?.[2] ?? '';
	const isOv = isOvRaw.toLowerCase() === 'true';
	const baseData = {
		digest1: map.digest1 ?? '',
		digest2: map.digest2 ?? '',
		fileName1: map.filename1 ?? '',
		fileName2: map.filename2 ?? '',
		componentId: map.componentid ?? '',
		attachmentId: map.attachmentid ?? '',
		mr: map.mr ?? '',
		requestId: map.requestid ?? '',
		signingTag: map.signingtag ?? '',
		withId: map.withid ?? '',
		digest: map.digest ?? '',
		digestArray: map.digestarray ?? '',
		fillInTheEnd: map.fillintheend ?? '',
		callbackUrl,
		isOv,
	};
	const fullData = {
		...baseData,
		digest1: baseData.digest1 || getValue('digest1'),
		digest2: baseData.digest2 || getValue('digest2'),
		fileName1: baseData.fileName1 || getValue('fileName1'),
		fileName2: baseData.fileName2 || getValue('fileName2'),
		componentId:
			baseData.componentId ||
			getValue('componentId') ||
			getValue('ComponentId'),
		attachmentId:
			baseData.attachmentId ||
			getValue('attachmentId') ||
			getValue('AttachmentId'),
		mr: baseData.mr || getValue('MR'),
		requestId: baseData.requestId || getValue('RequestId'),
		signingTag: baseData.signingTag || getValue('SigningTag'),
		withId: baseData.withId || getValue('WithId'),
		digest: baseData.digest || getValue('digest'),
		digestArray: baseData.digestArray || getValue('digestArray'),
		fillInTheEnd: baseData.fillInTheEnd || getValue('FillInTheEnd'),
		callbackUrl,
		isOv,
	};
	const mode =
		fullData.digest1 ||
		fullData.digest2 ||
		fullData.fileName1 ||
		fullData.fileName2
			? 'doubleSp'
			: 'base';
	return {
		mode,
		...fullData,
	};
};

const getRecordValue = (data: unknown, key: string): unknown => {
	if (!data || typeof data !== 'object') {
		return undefined;
	}
	const record = data as Record<string, unknown>;
	return record[key] ?? record[key[0].toUpperCase() + key.slice(1)];
};

const getNumberValue = (data: unknown, key: string): number => {
	const value = getRecordValue(data, key);
	const parsed = typeof value === 'number' ? value : Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const parseJson = <T>(value: string): T | null => {
	try {
		return JSON.parse(value) as T;
	} catch {
		return null;
	}
};

const formatErrorMessage = (error: unknown): string => {
	if (error instanceof Error && error.message) {
		return error.message;
	}
	if (typeof error === 'string') {
		return error;
	}
	if (error && typeof error === 'object' && 'message' in error) {
		const message = (error as { message?: unknown }).message;
		if (typeof message === 'string' && message) {
			return message;
		}
	}
	try {
		return JSON.stringify(error);
	} catch {
		return 'Ошибка при подписании';
	}
};

const normalizeSignXmlResult = (
	result: unknown,
): { sign: string; cert: string } => {
	const res = result as {
		sign?: string;
		cert?: string;
		data?: { sign?: string; cert?: string };
	};
	if (res?.sign) {
		return { sign: res.sign, cert: res.cert ?? '' };
	}
	if (res?.data?.sign) {
		return { sign: res.data.sign, cert: res.data.cert ?? '' };
	}
	return { sign: '', cert: '' };
};

const isGuidLike = (value: string): boolean => {
	return /^[0-9a-fA-F-]{36}$/.test(value);
};

const useSendSoapSignAttachment = ({
	fileRecId,
	info,
	onReload,
	needReload,
}: UseSendSoapSignAttachmentArgs) => {
	const { showWaiting, setWaiting } = useModal();
	const [isShowSign, toggleShowSign, setShowSign] = useToggle(false);
	const [signData, setSignData] = useState<SendSoapSignData | null>(null);

	const runWithWaiting = (action: () => Promise<void>) => {
		showWaiting('Подписание и отправка запроса');
		return action().finally(() => {
			return setWaiting(false);
		});
	};

	const onSignRequest = async () => {
		if (!info) {
			return;
		}
		return runWithWaiting(async () => {
			const responseText =
				await API.Attachment.requestSendSoapSignAttachment(
					fileRecId,
					info.componentId ?? '',
					info.attachmentId,
				);
			let htmlText = responseText;
			const trimmed = responseText.trim();
			if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
				const jsonData = JSON.parse(trimmed) as Record<string, unknown>;
				if (jsonData?.hasError) {
					API.showToast(
						false,
						String(
							jsonData.errorMessage ??
								'Ошибка при подписании и отправке запроса',
						),
					);
					return;
				}
				if (
					jsonData?.recid ||
					jsonData?.refreshattach ||
					jsonData?.refreshtask
				) {
					onReload();
					needReload();
					return;
				}
				if (typeof jsonData === 'string') {
					htmlText = jsonData;
				}
			}
			const parsed = parseSendSoapSignHtml(htmlText);
			if (!parsed.componentId) {
				API.showToast(
					false,
					'Не удалось подготовить данные для подписи',
				);
				return;
			}
			if (
				!parsed.digest1 &&
				!parsed.digest2 &&
				!parsed.digest &&
				!parsed.digestArray
			) {
				API.showToast(
					false,
					'Не удалось подготовить данные для подписи',
				);
				return;
			}
			setSignData(parsed);
			setShowSign(true);
		});
	};

	const handleSendSoapResponse = async (response: unknown) => {
		if (response && typeof response === 'object') {
			const hasError = Boolean(getRecordValue(response, 'hasError'));
			if (hasError) {
				API.showToast(
					false,
					String(
						getRecordValue(response, 'errorMessage') ??
							'Ошибка при подписании и отправке запроса',
					),
				);
				return;
			}
			const recid = getRecordValue(response, 'recid');
			const refreshattach = Boolean(
				getRecordValue(response, 'refreshattach'),
			);
			const refreshtask = Boolean(
				getRecordValue(response, 'refreshtask'),
			);
			if (recid || refreshattach || refreshtask) {
				onReload();
				needReload();
				return;
			}
		}
		if (typeof response !== 'string') {
			return;
		}
		const trimmed = response.trim();
		if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
			const parsedJson = parseJson<Record<string, unknown>>(trimmed);
			if (parsedJson) {
				await handleSendSoapResponse(parsedJson);
				return;
			}
		}
		const parsedHtml = parseSendSoapSignHtml(response);
		if (!parsedHtml.componentId) {
			API.showToast(false, 'Не удалось подготовить данные для подписи');
			return;
		}
		setSignData(parsedHtml);
		setShowSign(true);
	};

	const postSendSoapForm = async (
		url: string,
		data: Record<string, unknown>,
	) => {
		const params = new URLSearchParams();
		Object.entries(data).forEach(([key, value]) => {
			if (value === undefined || value === null) {
				return;
			}
			params.append(key, String(value));
		});
		const responseText = await API.Attachment.postSendSoapForm(url, params);
		await handleSendSoapResponse(responseText);
	};

	/** проверяем является ли сертификат квалифицированным*/
	const checkCertificateQualified = async (
		thumbprint: string,
	): Promise<boolean> => {
		const plugins = await API.Plugins.get();
		const denyUnqualified = Boolean(plugins?.DenyUnqualifiedSign);
		if (!denyUnqualified) {
			return true;
		}

		const qualified = await API.Certificate.getQualifiedThumbprints();
		return qualified.has(thumbprint);
	};

	const onSignFileHandler = async (cert: ICertificateInfo) => {
		if (!info || !signData) {
			return;
		}
		return runWithWaiting(async () => {
			try {
				const isQualified = await checkCertificateQualified(
					cert.thumbprint,
				);
				if (!isQualified) {
					throw new Error(
						'Выбранный сертификат не подходит для подписи. Пожалуйста, выберите другой сертификат',
					);
				}

				const certInfo = await API.Certificate.getCertInfoByThumbprint(
					cert.thumbprint,
				);
				const base64 = certInfo.base64;
				const componentId =
					signData.componentId || info.componentId || '';
				const attachmentId = signData.attachmentId || info.attachmentId;

				/** подписываем хеш и возвращаем подпись */
				const signHash = async (
					value: string,
					hashAlgId: number,
				): Promise<string> => {
					if (!value) {
						return '';
					}

					const signResult = await API.Certificate.signHash(
						value,
						hashAlgId,
						cert.thumbprint,
					);
					if (!signResult?.success) {
						throw new Error(
							signResult?.message ||
								'Выбранный сертификат не подходит для подписи. Пожалуйста, выберите другой сертификат',
						);
					}

					const signed =
						typeof signResult?.data === 'string'
							? signResult.data
							: '';
					if (!signed) {
						throw new Error(
							'Выбранный сертификат не подходит для подписи. Пожалуйста, выберите другой сертификат',
						);
					}

					return signed;
				};

				if (signData.mode === 'base') {
					const digestArrayRaw = signData.digestArray?.trim() ?? '';
					const digestArray = digestArrayRaw
						? parseJson<string[]>(digestArrayRaw)
						: null;
					const isAttachmentList =
						Array.isArray(digestArray) &&
						digestArray.length > 0 &&
						digestArray.every((item) => {
							return typeof item === 'string' && isGuidLike(item);
						});
					if (isAttachmentList) {
						const downloadResult =
							await API.Attachment.downloadSendSoapAttachmentsSmev3AndSign(
								{
									fileId: fileRecId,
									componentId,
									digestArray: digestArrayRaw,
									certificate: base64,
								},
							);
						if (downloadResult?.hasError) {
							throw new Error(
								downloadResult.errorMessage ??
									'Ошибка при подписании и отправке запроса',
							);
						}
						const hashAlgId = getNumberValue(
							downloadResult,
							'hashAlgId',
						);
						if (!hashAlgId) {
							throw new Error(
								'Выбранный сертификат не подходит для подписи вложения',
							);
						}
						const hashArrayRaw = String(
							getRecordValue(downloadResult, 'digestArray') ?? '',
						);
						const hashArray =
							parseJson<string[]>(hashArrayRaw) ?? [];
						if (hashArray.length === 0) {
							throw new Error('Не получены данные для подписи');
						}
						const signArray: string[] = [];
						for (const value of hashArray) {
							const signed = await signHash(value, hashAlgId);
							signArray.push(signed);
						}
						const callbackUrl =
							String(
								getRecordValue(
									downloadResult,
									'controllerCallbackMethodName',
								) ?? '',
							) || signData.callbackUrl;
						if (!callbackUrl) {
							throw new Error(
								'Не удалось определить маршрут завершения подписи',
							);
						}
						await postSendSoapForm(callbackUrl, {
							fileId: fileRecId,
							componentId,
							attachmentId,
							digestArray: digestArrayRaw,
							signArray: JSON.stringify(signArray),
							MR: signData.mr,
							SigningTag: signData.signingTag,
							WithId: signData.withId,
							FillInTheEnd: signData.fillInTheEnd,
						});
						return;
					}

					if (digestArray && digestArray.length > 0) {
						const signArray = [];
						for (const value of digestArray) {
							const result = await API.Certificate.signXmlPlugin(
								cert.thumbprint,
								value,
							);
							const signValue =
								normalizeSignXmlResult(result).sign;
							if (!signValue) {
								throw new Error(
									'Выбранный сертификат не подходит для подписи. Пожалуйста, выберите другой сертификат',
								);
							}
							signArray.push(signValue);
						}
						if (!signData.callbackUrl) {
							throw new Error(
								'Не удалось определить маршрут завершения подписи',
							);
						}
						await postSendSoapForm(signData.callbackUrl, {
							fileId: fileRecId,
							componentId,
							attachmentId,
							digestArray: JSON.stringify(signArray),
							ControllerCallbackMethodName: base64,
							MR: signData.mr,
							SigningTag: signData.signingTag,
							WithId: signData.withId,
							FillInTheEnd: signData.fillInTheEnd,
						});
						return;
					}

					const xmlSignResult = await API.Certificate.signXmlPlugin(
						cert.thumbprint,
						signData.digest,
					);
					const xmlSign = normalizeSignXmlResult(xmlSignResult);
					if (!xmlSign.sign) {
						throw new Error(
							'Выбранный сертификат не подходит для подписи. Пожалуйста, выберите другой сертификат',
						);
					}
					if (!signData.callbackUrl) {
						throw new Error(
							'Не удалось определить маршрут завершения подписи',
						);
					}
					await postSendSoapForm(signData.callbackUrl, {
						fileId: fileRecId,
						componentId,
						attachmentId,
						digest: xmlSign.sign,
						ControllerCallbackMethodName: xmlSign.cert || base64,
						MR: signData.mr,
						SigningTag: signData.signingTag,
						WithId: signData.withId,
						FillInTheEnd: signData.fillInTheEnd,
					});
					return;
				}

				const basePayload = {
					fileId: fileRecId,
					componentId,
					attachmentId,
					certificate: base64,
					digest1: signData.digest1,
					digest2: signData.digest2,
					fileName1: signData.fileName1,
					fileName2: signData.fileName2,
					requestId: signData.requestId,
					MR: signData.mr,
					signingTag: signData.signingTag,
					withId: signData.withId,
				};

				const downloadResult =
					await API.Attachment.downloadSendSoapAttachmentsAndSign(
						basePayload,
					);
				if (downloadResult?.hasError) {
					throw new Error(
						downloadResult.errorMessage ??
							'Ошибка при подписании и отправке запроса',
					);
				}
				const hashAlgId = getNumberValue(downloadResult, 'hashAlgId');
				if (!hashAlgId) {
					throw new Error(
						'Выбранный сертификат не подходит для подписи. Пожалуйста, выберите другой сертификат',
					);
				}

				const digest1 = String(
					getRecordValue(downloadResult, 'digest1') ?? '',
				);
				const digest2 = String(
					getRecordValue(downloadResult, 'digest2') ?? '',
				);
				const innerRequestHash = String(
					getRecordValue(downloadResult, 'innerRequestHash') ?? '',
				);
				const innerRequestFileName = String(
					getRecordValue(downloadResult, 'innerRequestFileName') ??
						'',
				);

				const signedDigest1 = await signHash(digest1, hashAlgId);
				let signedDigest2 = await signHash(digest2, hashAlgId);
				const signedInnerRequest = await signHash(
					innerRequestHash,
					hashAlgId,
				);
				let finalInnerRequestSign = signedInnerRequest;
				let finalDigest2 = signedDigest2;

				if (digest2) {
					const descrRequest = {
						fileId: basePayload.fileId,
						componentId: basePayload.componentId,
						attachmentId: basePayload.attachmentId,
						certificate: base64,
						digest1: signedDigest1,
						innerRequestSign: signedInnerRequest,
						innerRequestFileName,
						requestId: basePayload.requestId,
						MR: basePayload.MR,
					};
					const descrResult =
						basePayload.MR === 'MR300'
							? await API.Attachment.getSendSoapAttachmentsArchiveDescriptionHashSmev3(
									descrRequest,
								)
							: await API.Attachment.getSendSoapAttachmentsArchiveDescriptionHash(
									descrRequest,
								);
					if (descrResult?.hasError) {
						throw new Error(
							descrResult.errorMessage ??
								'Ошибка при подписании и отправке запроса',
						);
					}
					const nextDigest2 = String(
						getRecordValue(descrResult, 'digest2') ?? '',
					);
					const nextHashAlgId =
						getNumberValue(descrResult, 'hashAlgId') || hashAlgId;
					finalInnerRequestSign =
						String(
							getRecordValue(descrResult, 'innerRequestSign') ??
								'',
						) || signedInnerRequest;
					if (nextDigest2) {
						signedDigest2 = await signHash(
							nextDigest2,
							nextHashAlgId,
						);
						finalDigest2 = signedDigest2;
					}
				}

				const completeResult =
					await API.Attachment.completeSendSoapAttachmentsArchive({
						fileId: basePayload.fileId,
						componentId: basePayload.componentId,
						attachmentId: basePayload.attachmentId,
						digest1: signedDigest1,
						digest2: finalDigest2,
						innerRequestSign: finalInnerRequestSign,
						innerRequestFileName,
						requestId: basePayload.requestId,
						MR: basePayload.MR,
					});

				if (completeResult?.hasError) {
					throw new Error(
						completeResult.errorMessage ??
							'Ошибка при подписании и отправке запроса',
					);
				}

				onReload();
				needReload();
			} catch (error) {
				API.showToast(false, formatErrorMessage(error));
			}
		});
	};

	return {
		isShowSign,
		toggleShowSign,
		onSignRequest,
		onSignFileHandler,
	};
};

export default useSendSoapSignAttachment;
